import { useState, useCallback } from 'react'
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native'
import { CheckCircle } from 'lucide-react-native'
import { BaseModal } from '../../ui/BaseModal'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useDialog } from '../../../src/shared/hooks/useDialog'
import {
  sanitizeInput,
  sanitizeEmailInput,
  EMAIL_REGEX,
  MAX_MESSAGE_LENGTH,
} from '../../../src/helpers/sanitizeInput.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { supportModalStyles as styles } from './SupportModal.styles'

const MAX_SUBJECT_LENGTH = 100

const CATEGORIES = [
  { key: 'bug', label: 'Bug' },
  { key: 'complaint', label: 'Complaint' },
  { key: 'suggestion', label: 'Suggestion' },
  { key: 'other', label: 'Other' },
] as const

type Category = (typeof CATEGORIES)[number]['key']

interface SupportModalProps {
  isVisible: boolean
  onDismiss: () => void
  testID?: string
}

export function SupportModal({ isVisible, onDismiss, testID }: SupportModalProps) {
  const { user } = useSupabaseAuth()
  const apiClient = useApiClient()
  const { showAlert } = useDialog()

  const accountEmail = user?.primaryEmailAddress?.emailAddress ?? ''

  const [category, setCategory] = useState<Category>('complaint')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [subjectFocused, setSubjectFocused] = useState(false)
  const [messageFocused, setMessageFocused] = useState(false)
  const [guestEmailFocused, setGuestEmailFocused] = useState(false)
  const [errors, setErrors] = useState<{ subject?: string; message?: string; email?: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const resolvedEmail = accountEmail || guestEmail

  const handleDismiss = useCallback(() => {
    setCategory('complaint')
    setSubject('')
    setMessage('')
    setGuestEmail('')
    setErrors({})
    setIsSuccess(false)
    onDismiss()
  }, [onDismiss])

  const validate = useCallback(() => {
    const next: typeof errors = {}
    if (!subject.trim()) next.subject = 'Subject is required.'
    if (!message.trim()) next.message = 'Message is required.'
    if (!accountEmail && (!guestEmail.trim() || !EMAIL_REGEX.test(guestEmail.trim()))) {
      next.email = 'Enter a valid email address.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }, [subject, message, accountEmail, guestEmail])

  const handleSubmit = useCallback(async () => {
    if (!validate()) return
    setIsLoading(true)
    try {
      const response = await apiClient(API_ENDPOINTS.support.submit, {
        method: 'POST',
        data: {
          category,
          subject: sanitizeInput(subject.trim()),
          message: sanitizeInput(message.trim(), true),
          email: sanitizeEmailInput(resolvedEmail.trim()),
        },
      })
      const result = response.data as { success: boolean; message?: string }
      if (result.success) {
        setIsSuccess(true)
      } else {
        await showAlert({
          title: 'Could not send',
          message: result.message || 'Something went wrong. Please try again.',
          type: 'danger',
        })
      }
    } catch {
      await showAlert({
        title: 'Could not send',
        message: 'Something went wrong. Please try again.',
        type: 'danger',
      })
    } finally {
      setIsLoading(false)
    }
  }, [validate, apiClient, category, subject, message, resolvedEmail, showAlert])

  if (!isVisible) return null

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={handleDismiss}
      contentAlignment="center"
      contentContainerStyle={styles.modalOuter}
      testID={testID ?? 'support-modal'}
      contentTestID={testID ? `${testID}-content` : 'support-modal-content'}
    >
      <View style={styles.sheet}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {isSuccess ? (
          <View style={styles.successContainer}>
            <CheckCircle size={48} color="#4ade80" />
            <Text style={styles.successTitle}>Message sent!</Text>
            <Text style={styles.successMessage}>
              We received your message and will follow up at {resolvedEmail}.
            </Text>
            <Pressable
              style={({ pressed }) => [styles.submitButton, pressed && styles.submitButtonPressed]}
              onPress={handleDismiss}
            >
              <Text style={styles.submitButtonText}>Done</Text>
            </Pressable>
          </View>
        ) : (
          <>
            <Text style={styles.title}>Contact Support</Text>
            <Text style={styles.subtitle}>Tell us what's on your mind and we'll get back to you.</Text>

            <Text style={styles.label}>Category</Text>
            <View style={styles.categoryRow}>
              {CATEGORIES.map((c) => (
                <Pressable
                  key={c.key}
                  style={({ pressed }) => [
                    styles.categoryChip,
                    category === c.key && styles.categoryChipActive,
                    pressed && styles.categoryChipPressed,
                  ]}
                  onPress={() => setCategory(c.key)}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: category === c.key }}
                  testID={testID ? `${testID}-category-${c.key}` : `support-modal-category-${c.key}`}
                >
                  <Text
                    style={[
                      styles.categoryChipText,
                      category === c.key && styles.categoryChipTextActive,
                    ]}
                  >
                    {c.label}
                  </Text>
                </Pressable>
              ))}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Subject</Text>
              <TextInput
                style={[styles.textInput, subjectFocused && styles.textInputFocused]}
                value={subject}
                onChangeText={(t) => {
                  setSubject(sanitizeInput(t, true).slice(0, MAX_SUBJECT_LENGTH))
                  if (errors.subject) setErrors((e) => ({ ...e, subject: undefined }))
                }}
                onFocus={() => setSubjectFocused(true)}
                onBlur={() => setSubjectFocused(false)}
                placeholder="Brief summary of your issue"
                placeholderTextColor="#6B7280"
                returnKeyType="next"
                maxLength={MAX_SUBJECT_LENGTH}
                testID={testID ? `${testID}-subject-input` : 'support-modal-subject-input'}
              />
              {errors.subject ? <Text style={styles.errorText}>{errors.subject}</Text> : null}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Message</Text>
              <TextInput
                style={[styles.textInput, styles.messageInput, messageFocused && styles.textInputFocused]}
                value={message}
                onChangeText={(t) => {
                  setMessage(sanitizeInput(t, true).slice(0, MAX_MESSAGE_LENGTH))
                  if (errors.message) setErrors((e) => ({ ...e, message: undefined }))
                }}
                onFocus={() => setMessageFocused(true)}
                onBlur={() => setMessageFocused(false)}
                placeholder="Describe your issue or feedback in detail"
                placeholderTextColor="#6B7280"
                multiline
                numberOfLines={4}
                maxLength={MAX_MESSAGE_LENGTH}
                testID={testID ? `${testID}-message-input` : 'support-modal-message-input'}
              />
              <Text style={styles.charCount}>{message.length}/{MAX_MESSAGE_LENGTH}</Text>
              {errors.message ? <Text style={styles.errorText}>{errors.message}</Text> : null}
            </View>

            {!accountEmail && (
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Reply-to email</Text>
                <TextInput
                  style={[styles.textInput, guestEmailFocused && styles.textInputFocused]}
                  value={guestEmail}
                  onChangeText={(t) => {
                    setGuestEmail(sanitizeEmailInput(t))
                    if (errors.email) setErrors((e) => ({ ...e, email: undefined }))
                  }}
                  onFocus={() => setGuestEmailFocused(true)}
                  onBlur={() => setGuestEmailFocused(false)}
                  placeholder="your@email.com"
                  placeholderTextColor="#6B7280"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  testID={testID ? `${testID}-email-input` : 'support-modal-email-input'}
                />
                {errors.email ? <Text style={styles.errorText}>{errors.email}</Text> : null}
              </View>
            )}

            <Pressable
              style={({ pressed }) => [
                styles.submitButton,
                isLoading && styles.submitButtonDisabled,
                pressed && !isLoading && styles.submitButtonPressed,
              ]}
              onPress={() => { void handleSubmit() }}
              disabled={isLoading}
              testID={testID ? `${testID}-submit-button` : 'support-modal-submit-button'}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={styles.submitButtonText}>Send message</Text>
              )}
            </Pressable>

            <Pressable
              style={({ pressed }) => [styles.closeButton, pressed && styles.closeButtonPressed]}
              onPress={handleDismiss}
              testID={testID ? `${testID}-cancel-button` : 'support-modal-cancel-button'}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </Pressable>
          </>
        )}
      </ScrollView>
      </View>
    </BaseModal>
  )
}
